import SelectableRangeCard from "../SelectableRangeCard";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
    selectCommercialCartSelection,
    selectDataSuggested,
} from "src/redux/shopping_cart/shopping_cart.selectors";
import {
    setEliteZone,
    setInputError,
    setSelectionPrice,
    setSelectionQuantity,
    toggleProductSelection,
} from "src/redux/shopping_cart/shopping_cart.slice";
import { CategoryProducts, ProductByCategory } from "src/redux/shopping_cart/shopping_cart.initial";
import { isPremiumMonthSelectionInvalid } from "src/redux/shopping_cart/shopping_cart.constants";
import {
    AutoSelectRule,
    ELITE_ZONE_PRICES,
    SECTIONS_CONFIG,
    SectionCategoryKeys,
    Subcategory,
    SUBCATEGORIES_WITHOUT_QUANTITY_INPUT,
    SUBCATEGORIES_WITH_PRICE_SCALED_BY_QUANTITY,
    SUBCATEGORY_AUTO_SELECT_RULES,
    SUBCATEGORY_DISABLE_RULES,
    SUBCATEGORY_VIEW_CONFIG,
    SubcategoryViewConfigEntry,
} from "./CommercialCart.constants";
import { ProducsSelectableContainer } from "./ProducsSelectable.styles";

/**
 * Obtiene la config de vista (icono, colores, quantity/price min-max-default) para una subcategoría.
 * Si la subcategoría está en SUBCATEGORY_VIEW_CONFIG (CommercialCart.constants), devuelve esa config.
 * Si no está registrada (p. ej. el API devolvió un producto nuevo que aún no se añadió en constants),
 * devuelve un fallback con icono/colores de la sección y rangos genéricos (1–999999) para no romper la UI.
 * El flujo preferido es registrar cada producto/categoría en constants (tipos, SUBCATEGORY_VIEW_CONFIG y
 * SECTIONS_CONFIG) antes de mostrarlo; este fallback actúa solo como red de seguridad.
 */
function getViewConfigWithRange(
    subcategoryKey: Subcategory | undefined,
    section: { icon: string; colors: SubcategoryViewConfigEntry["colors"] }
): SubcategoryViewConfigEntry {
    const fromConfig = subcategoryKey && SUBCATEGORY_VIEW_CONFIG[subcategoryKey];
    if (fromConfig) return fromConfig;
    const fallbackRange = { min: 1, max: 999999, default: 1 } as const;
    return {
        icon: section.icon,
        colors: section.colors,
        quantity: fallbackRange,
        price: fallbackRange,
    };
}

const ProducsSelectable = () => {
    const dispatch = useAppDispatch();
    const { suggested } = useAppSelector(selectDataSuggested);
    const { selectedByProductId, quantityByProductId, priceByProductId, selectedMonths, eliteZoneByProductId } = useAppSelector(selectCommercialCartSelection);

    const getAllProducts = (): ProductByCategory[] =>
        Object.values(suggested.products.category).flat();

    const toggleSelected = (product: ProductByCategory, viewConfig: SubcategoryViewConfigEntry) => {
        const productId = product.product_id;
        const subcategoryKey = product.subcategory?.toLowerCase() as Subcategory | undefined;
        const willBeSelected = !selectedByProductId[productId];
        let productIdsToClear: number[] = [];
        if (willBeSelected && subcategoryKey && SUBCATEGORY_DISABLE_RULES[subcategoryKey]) {
            productIdsToClear = getAllProducts()
                .filter((p) =>
                    SUBCATEGORY_DISABLE_RULES[subcategoryKey].includes(
                        (p.subcategory?.toLowerCase() ?? '') as Subcategory
                    )
                )
                .map((p) => p.product_id);
        }
        if (!willBeSelected && subcategoryKey) {
            const autoSelectRules = SUBCATEGORY_AUTO_SELECT_RULES[subcategoryKey];
            if (autoSelectRules) {
                const autoSelectedIds = getAllProducts()
                    .filter((p) =>
                        autoSelectRules.some((r) => r.subcategory === (p.subcategory?.toLowerCase() ?? ''))
                    )
                    .map((p) => p.product_id);
                productIdsToClear = [...productIdsToClear, ...autoSelectedIds];
            }
        }
        dispatch(toggleProductSelection({
            productId,
            willBeSelected,
            productIdsToClear,
            initialQuantity: viewConfig.quantity.default,
            initialPrice: viewConfig.price.default,
        }));

        // Auto-seleccionar productos según SUBCATEGORY_AUTO_SELECT_RULES
        const autoSelectRules = willBeSelected && subcategoryKey
            ? SUBCATEGORY_AUTO_SELECT_RULES[subcategoryKey]
            : undefined;
        if (autoSelectRules) {
            const allProducts = getAllProducts();
            autoSelectRules.forEach((rule) => {
                const matchingProducts = allProducts.filter(
                    (p) => (p.subcategory?.toLowerCase() ?? '') === rule.subcategory
                );
                matchingProducts.forEach((p) => {
                    const alreadySelected = !!selectedByProductId[p.product_id];

                    if (!alreadySelected) {
                        // Producto no seleccionado → auto-seleccionar con overrides
                        const autoSubcategory = p.subcategory?.toLowerCase() as Subcategory | undefined;
                        const autoViewConfig = getViewConfigWithRange(autoSubcategory, {
                            icon: viewConfig.icon,
                            colors: viewConfig.colors,
                        });
                        dispatch(toggleProductSelection({
                            productId: p.product_id,
                            willBeSelected: true,
                            productIdsToClear: [],
                            initialQuantity: rule.quantity?.default ?? autoViewConfig.quantity.default,
                            initialPrice: rule.price?.default ?? autoViewConfig.price.default,
                        }));
                    } else {
                        // Producto ya seleccionado → forzar overrides de precio/cantidad
                        if (rule.price) dispatch(setSelectionPrice({ productId: p.product_id, value: rule.price.default }));
                        if (rule.quantity) dispatch(setSelectionQuantity({ productId: p.product_id, value: rule.quantity.default }));
                    }
                });
            });
        }
    };
    const setPriceForProduct = (productId: number) => (value: number) => {
        dispatch(setSelectionPrice({ productId, value }));
    };

    function getProductsForSection(
        category: CategoryProducts,
        categoryKeys: SectionCategoryKeys
    ): ProductByCategory[] {
        // retornar los productos de las categorias que se pasan
        // como parametro
        return categoryKeys.flatMap((key) => category[key] ?? []);
    }

    const getDisabledSubcategories = (): Set<string> => {
        const allProducts = getAllProducts();
        const disabled = new Set<string>();
        allProducts.forEach((p) => {
            if (!selectedByProductId[p.product_id]) return;
            const sub = (p.subcategory?.toLowerCase() ?? '') as Subcategory;
            const toDisable = SUBCATEGORY_DISABLE_RULES[sub];
            if (toDisable) toDisable.forEach((s) => disabled.add(s));
        });
        return disabled;
    };

    /**
     * Busca si alguna subcategoría seleccionada tiene una regla de auto-select
     * que aplique overrides (quantity/price) sobre la subcategoría del producto dado.
     * Retorna la primera regla que haga match, o undefined si no hay ninguna activa.
     */
    const getActiveAutoSelectRule = (product: ProductByCategory): AutoSelectRule | undefined => {
        const targetSub = (product.subcategory?.toLowerCase() ?? '') as Subcategory;
        const allProducts = getAllProducts();
        for (const p of allProducts) {
            if (!selectedByProductId[p.product_id]) continue;
            const sourceSub = (p.subcategory?.toLowerCase() ?? '') as Subcategory;
            const rules = SUBCATEGORY_AUTO_SELECT_RULES[sourceSub];
            if (!rules) continue;
            const match = rules.find((r) => r.subcategory === targetSub);
            if (match && (match.quantity || match.price)) return match;
        }
        return undefined;
    };

    const getComponentSelectable = (
        product: ProductByCategory,
        viewConfig: SubcategoryViewConfigEntry,
        disabled: boolean
    ) => {
        const productId = product.product_id;
        const subcategoryKey = product.subcategory?.toLowerCase() as Subcategory | undefined;
        const isSelected = selectedByProductId[productId] ?? false;
        const activeRule = isSelected ? getActiveAutoSelectRule(product) : undefined;
        const quantity = quantityByProductId[productId] ?? viewConfig.quantity.default;
        const price = priceByProductId[productId] ?? viewConfig.price.default;
        const qConfig = activeRule?.quantity ?? viewConfig.quantity;
        const pConfig = activeRule?.price ?? viewConfig.price;

        const isLockedByAutoSelect = isSelected && !!activeRule;

        const showQuantityField =
            !subcategoryKey || !SUBCATEGORIES_WITHOUT_QUANTITY_INPUT.has(subcategoryKey);

        const isPriceScaledByQuantity =
            !!subcategoryKey &&
            SUBCATEGORIES_WITH_PRICE_SCALED_BY_QUANTITY.has(subcategoryKey) &&
            !isLockedByAutoSelect;

        // Lógica específica de zona para Elite
        const isElite = subcategoryKey === 'elite';
        const eliteZone = isElite ? (eliteZoneByProductId[productId] ?? null) : null;
        const eliteZoneMinPrice = eliteZone
            ? (ELITE_ZONE_PRICES.find((z) => z.label === eliteZone)?.minPrice ?? pConfig.min)
            : pConfig.min;
        const scaledPrice = isPriceScaledByQuantity
            ? pConfig.min * quantity
            : pConfig.min;
        const effectivePriceMin = isElite ? eliteZoneMinPrice : scaledPrice;

        /** Texto fijo al mínimo unitario; el input sigue validando contra `effectivePriceMin` (total). */
        const priceHelperText = isPriceScaledByQuantity
            ? `Mín: $${pConfig.min}`
            : `Mín: $${effectivePriceMin}`;

        const handleQuantityChange = (nextQty: number) => {
            dispatch(setSelectionQuantity({ productId, value: nextQty }));
            if (isPriceScaledByQuantity) {
                const unitMin = pConfig.min;
                const scaled = unitMin * nextQty;
                dispatch(setSelectionPrice({ productId, value: Math.min(scaled, pConfig.max) }));
            }
        };

        const handleEliteZoneChange = (zone: string) => {
            dispatch(setEliteZone({ productId, zone }));
            const newMinPrice = ELITE_ZONE_PRICES.find((z) => z.label === zone)?.minPrice ?? pConfig.min;
            dispatch(setSelectionPrice({ productId, value: newMinPrice }));
            dispatch(setInputError({ productId, field: 'elite_zone', hasError: false }));
        };

        return (
            <SelectableRangeCard
                selected={isSelected}
                onToggle={() => {
                    if (isLockedByAutoSelect) return;
                    toggleSelected(product, viewConfig);
                }}
                borderColor={viewConfig.colors.borderColor}
                backgroundColor={viewConfig.colors.backgroundColor}
                checkColor={viewConfig.colors.checkColor}
                icon={viewConfig.icon}
                title={product.name_product}
                description={product.description}
                disabled={disabled}
                onSectionError={(sectionId, hasError) => {
                    dispatch(setInputError({
                        productId,
                        field: sectionId as 'price' | 'quantity' | 'elite_zone',
                        hasError,
                    }));
                }}
                dropdownSection={isElite ? {
                    id: 'elite_zone',
                    label: 'Zona',
                    value: eliteZone,
                    options: ELITE_ZONE_PRICES.map((z) => ({ label: z.label, value: z.label })),
                    onChange: handleEliteZoneChange,
                } : undefined}
                sections={[
                    ...(showQuantityField
                        ? [{
                            id: "quantity" as const,
                            label: "Cantidad",
                            value: quantity,
                            onChange: handleQuantityChange,
                            minAllowed: qConfig.min,
                            maxAllowed: qConfig.max,
                        }]
                        : []),
                    ...(!isElite || eliteZone !== null ? [{
                        id: "price",
                        label: "Precio",
                        value: price,
                        onChange: setPriceForProduct(productId),
                        minAllowed: effectivePriceMin,
                        maxAllowed: pConfig.max,
                        prefix: "$",
                        helperText: priceHelperText,
                        ...(isPriceScaledByQuantity ? { emptyFieldErrorMinDisplay: pConfig.min } : {}),
                    }] : []),
                ]}
            />
        );
    }

    return (
        <>
            <h1>Catálogo de productos</h1>
            {SECTIONS_CONFIG.map(({ sectionId, title, categoryKeys, colors, icon }) => {
                const products = getProductsForSection(suggested.products.category, categoryKeys);
                if (products.length === 0) return null;

                return (
                    <ProducsSelectableContainer key={sectionId}>
                        <h2 className="title">{title} <span className="quantity">({products.length})</span></h2>
                        <div className="products-container">
                            {products.map((product) => {
                                const subcategoryKey = product.subcategory?.toLowerCase() as Subcategory | undefined;
                                const viewConfig = getViewConfigWithRange(subcategoryKey, { icon, colors });
                                const disabledSubcategories = getDisabledSubcategories();
                                if (subcategoryKey === 'leads') return null;
                                const isDisabledBySubcategory = !!subcategoryKey && disabledSubcategories.has(subcategoryKey);
                                const isPremiumDisabledByMonths =
                                    subcategoryKey === 'premium' && isPremiumMonthSelectionInvalid(selectedMonths);
                                const isPremiumDisabledByPoc =
                                    subcategoryKey === 'premium' && suggested.user_profile?.freemium_state === null;
                                const isDisabled = isDisabledBySubcategory || isPremiumDisabledByMonths || isPremiumDisabledByPoc;
                                return (
                                    <div className="product" key={product.product_id}>
                                        {getComponentSelectable(product, viewConfig, isDisabled)}
                                    </div>
                                );
                            })}
                        </div>
                    </ProducsSelectableContainer>
                );
            })}
        </>
    );
};

export default ProducsSelectable;